import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { treatmentServices } from '@/mocks/treatmentServices';
import { cities } from '@/mocks/cities';
import { supabase } from '@/lib/supabase';

interface Institution {
  id: string;
  name: string;
  city: string | null;
  type: string | null;
  is_pro_member: boolean;
}

interface Agency {
  id: string;
  name: string;
  city: string | null;
  logo_url: string | null;
}

const BG_IMAGES = [
  'https://public.readdy.ai/ai/img_res/ca107d82b83af46dec79788c4c408844.jpg',
  'https://readdy.ai/api/search-image?query=state%20of%20the%20art%20medical%20imaging%20center%20with%20large%20white%20MRI%20machine%20glowing%20with%20soft%20white%20light%2C%20radiologist%20reviewing%20brain%20scans%20on%20bright%20white%20monitors%2C%20very%20bright%20white%20clinical%20room%20flooded%20with%20natural%20daylight%20from%20large%20windows%2C%20white%20walls%20white%20floors%2C%20ultra%20high%20key%20bright%20airy%20medical%20photography%2C%20luminous%20clean%20futuristic%20healthcare%20technology%2C%20no%20dark%20tones&width=1920&height=1080&seq=hero-v408-mri-bright-22&orientation=landscape',
  'https://readdy.ai/api/search-image?query=luxury%20private%20hospital%20room%20with%20panoramic%20floor%20to%20ceiling%20windows%20overlooking%20sunny%20city%20skyline%2C%20warm%20golden%20sunlight%20flooding%20entire%20room%2C%20white%20and%20cream%20premium%20interior%20design%2C%20modern%20medical%20equipment%20beside%20elegant%20comfortable%20patient%20bed%2C%20fresh%20flowers%20on%20bedside%20table%2C%20ultra%20bright%20high%20key%20airy%20premium%20healthcare%20suite%20photography%2C%20luminous%20elegant%20warm%20medical%20environment&width=1920&height=1080&seq=hero-v408-room-bright-33&orientation=landscape',
];

export default function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [bgIndex, setBgIndex] = useState(0);

  const [selectedTreatment, setSelectedTreatment] = useState('');
  const [showTreatmentDropdown, setShowTreatmentDropdown] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedHealthInstitution, setSelectedHealthInstitution] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [organizationMode, setOrganizationMode] = useState<'self' | 'agency' | null>(null);
  const [healthInstitutions, setHealthInstitutions] = useState<Institution[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [loadingAgencies, setLoadingAgencies] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % BG_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedTreatment) { setHealthInstitutions([]); return; }
    const load = async () => {
      setLoadingInstitutions(true);
      try {
        const { data } = await supabase
          .from('institutions')
          .select('id, name, city, type, is_pro_member')
          .eq('is_approved', true)
          .neq('type', 'agency')
          .order('name', { ascending: true });
        if (data) setHealthInstitutions(data);
      } catch (err) { console.error(err); }
      setLoadingInstitutions(false);
    };
    load();
  }, [selectedTreatment]);

  useEffect(() => {
    const load = async () => {
      setLoadingAgencies(true);
      try {
        const { data } = await supabase
          .from('institutions')
          .select('id, name, city, logo_url')
          .eq('is_approved', true)
          .eq('type', 'agency')
          .order('name', { ascending: true });
        if (data) setAgencies(data);
      } catch (err) { console.error(err); }
      setLoadingAgencies(false);
    };
    load();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowTreatmentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    if (!selectedTreatment) return;
    const params = new URLSearchParams();
    const treatmentService = treatmentServices.find((s) => t(s.titleKey) === selectedTreatment);
    if (treatmentService) params.append('service', treatmentService.id);
    if (selectedCity) params.append('city', selectedCity);
    if (organizationMode === 'self' && selectedHealthInstitution) {
      navigate(`/kurum/${selectedHealthInstitution}`);
    } else if (organizationMode === 'agency' && selectedAgency) {
      navigate(`/kurum/${selectedAgency}`);
    } else if (organizationMode === 'agency') {
      navigate(`/araci-kurumlar?${params.toString()}`);
    } else {
      navigate(`/saglik-kurumlari?${params.toString()}`);
    }
  };

  const clearAll = () => {
    setSelectedTreatment('');
    setOrganizationMode(null);
    setSelectedHealthInstitution('');
    setSelectedAgency('');
    setSelectedCity('');
  };

  const stats = [
    { icon: 'ri-hospital-line', value: '500+', label: t('hero_stat_institutions') },
    { icon: 'ri-user-heart-line', value: '50K+', label: t('hero_stat_patients') },
    { icon: 'ri-stethoscope-line', value: '2.000+', label: t('hero_stat_doctors') },
    { icon: 'ri-global-line', value: '80+', label: t('hero_stat_countries') },
  ];

  return (
    <section className="relative overflow-hidden" style={{ minHeight: '100svh' }}>
      {BG_IMAGES.map((src, i) => (
        <div key={i} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: i === bgIndex ? 1 : 0 }}>
          <img src={src} alt="Health Travel Türkiye" className="w-full h-full object-cover object-center" />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/30 to-black/10"></div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {BG_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setBgIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === bgIndex ? 'w-8 bg-white' : 'w-2 bg-white/50'}`}
          />
        ))}
      </div>

      <div
        className="relative z-10 w-full h-full flex items-center px-4 sm:px-6 lg:px-8"
        style={{ minHeight: '100svh', paddingTop: 'var(--navbar-height, 120px)', paddingBottom: '80px' }}
      >
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left */}
          <div className="text-left">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold rounded-full mb-6 uppercase tracking-widest">
              <span className="text-2xl">🇹🇷</span>
              {t('hero_badge')}
            </span>

            <h1 className="font-['Outfit'] mb-6">
              <span className="block text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight drop-shadow-lg">
                {t('hero_title_1')}
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mt-1">
                <span className="text-rose-300 drop-shadow-lg">{t('hero_title_2')}</span>
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mt-1 drop-shadow-lg">
                {t('hero_title_3')}
              </span>
            </h1>

            <p className="text-base sm:text-lg text-white/90 mb-10 max-w-xl leading-relaxed font-light drop-shadow">
              {t('hero_subtitle')}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-white/20 backdrop-blur-md border border-white/30 rounded-2xl p-4 text-center">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className={`${stat.icon} text-rose-300 text-xl`}></i>
                  </div>
                  <div className="text-2xl font-bold text-white font-['Outfit'] drop-shadow">{stat.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <span className="text-xs text-white/60 uppercase tracking-wider mb-3 block">{t('hero_accreditation_label')}</span>
              <div className="flex flex-col gap-2">
                {[
                  {
                    logo: 'https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/f60d29a1-c69a-4180-b9bf-f3bf3f7fe15d_Republic-of-Turkiye-Ministry-of-Health-Logo.png?v=71e00ccd8f6d40e8d4801bf4a26e4341',
                    labelKey: 'ministry_health',
                  },
                  {
                    logo: 'https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d3120357-b604-44a0-aefb-b4527d8be74a_Health-Turkiye-Logo.svg?v=733eda141137079f8cc11e4f506bec91',
                    labelKey: 'health_turkey',
                  },
                  {
                    logo: 'https://storage.readdy-site.link/project_files/b825b40c-ced1-45bc-acd7-e75b219f4e94/d8a5e1d4-49c4-4acf-997a-18d6539223bc_Republic-of-Turkiye-Ministry-of-Cultureand-Tourism-Logo.png?v=1f9b9ce043aa351352a5d6d7c5120a9b',
                    labelKey: 'ministry_culture',
                  },
                ].map((item) => (
                  <div
                    key={item.labelKey}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl backdrop-blur-sm"
                    style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.20)' }}
                  >
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg flex-shrink-0 p-1">
                      <img src={item.logo} alt={t(item.labelKey)} className="w-full h-full object-contain" />
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-white text-xs font-medium leading-tight">{t(item.labelKey)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Search Card */}
          <div>
            <div
              className="rounded-3xl p-6 md:p-8"
              style={{
                background: 'rgba(10,20,40,0.55)',
                backdropFilter: 'blur(28px)',
                WebkitBackdropFilter: 'blur(28px)',
                border: '1px solid rgba(255,255,255,0.18)',
                boxShadow: '0 8px 48px rgba(0,0,0,0.25)',
              }}
            >
              <h3 className="text-lg font-bold text-white mb-5 font-['Outfit']">
                <i className="ri-search-2-line mr-2 text-rose-300"></i>
                {t('hero_search_title')}
              </h3>

              {/* Step 1 */}
              <div className="mb-4">
                <label className="block text-left text-xs font-semibold text-white/70 mb-2 uppercase tracking-wider">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-rose-500 text-white text-[10px] rounded-full mr-2">1</span>
                  {t('hero_step1_label')}
                </label>
                <div className="relative" ref={dropdownRef}>
                  <div className="relative flex items-center">
                    <i className="ri-stethoscope-line absolute left-3.5 text-white/50 text-sm pointer-events-none"></i>
                    <input
                      type="text"
                      value={selectedTreatment}
                      onChange={(e) => { setSelectedTreatment(e.target.value); setShowTreatmentDropdown(true); }}
                      onFocus={() => setShowTreatmentDropdown(true)}
                      placeholder={t('hero_step1_placeholder')}
                      className="w-full pl-9 pr-10 py-3.5 rounded-xl text-sm text-white placeholder-white/40 outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)' }}
                    />
                    {selectedTreatment && (
                      <button type="button" onClick={() => { setSelectedTreatment(''); setShowTreatmentDropdown(false); }} className="absolute right-3 text-white/40 hover:text-white/80 cursor-pointer transition-colors">
                        <i className="ri-close-line text-base"></i>
                      </button>
                    )}
                  </div>
                  {showTreatmentDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50 max-h-60 overflow-y-auto" style={{ boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }}>
                      <div className="p-2">
                        {treatmentServices
                          .filter((s) => !selectedTreatment || t(s.titleKey).toLowerCase().includes(selectedTreatment.toLowerCase()))
                          .map((service) => (
                            <button
                              key={service.id}
                              type="button"
                              onMouseDown={() => { setSelectedTreatment(t(service.titleKey)); setShowTreatmentDropdown(false); }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-rose-50 transition-colors rounded-xl cursor-pointer"
                            >
                              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
                                <i className={`${service.icon} text-gray-600 text-sm`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">{t(service.titleKey)}</div>
                                <div className="text-xs text-gray-400 truncate">{t(service.subtitleKey)}</div>
                              </div>
                              {selectedTreatment === t(service.titleKey) && <i className="ri-check-line text-rose-500 flex-shrink-0"></i>}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 2 */}
              <div className="mb-4">
                <label className="block text-left text-xs font-semibold text-white/70 mb-2 uppercase tracking-wider">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-rose-500 text-white text-[10px] rounded-full mr-2">2</span>
                  {t('hero_step2_label')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setOrganizationMode(organizationMode === 'self' ? null : 'self'); setSelectedAgency(''); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl text-center transition-all cursor-pointer"
                    style={{ background: organizationMode === 'self' ? 'rgba(244,63,94,0.30)' : 'rgba(255,255,255,0.08)', border: organizationMode === 'self' ? '1.5px solid rgba(244,63,94,0.7)' : '1px solid rgba(255,255,255,0.15)' }}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${organizationMode === 'self' ? 'bg-rose-500 text-white' : 'bg-white/15 text-white/70'}`}>
                      <i className="ri-user-line text-base"></i>
                    </div>
                    <div className="text-xs font-semibold text-white leading-tight">{t('hero_self_organize')}</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => { setOrganizationMode(organizationMode === 'agency' ? null : 'agency'); setSelectedHealthInstitution(''); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl text-center transition-all cursor-pointer"
                    style={{ background: organizationMode === 'agency' ? 'rgba(20,184,166,0.30)' : 'rgba(255,255,255,0.08)', border: organizationMode === 'agency' ? '1.5px solid rgba(20,184,166,0.7)' : '1px solid rgba(255,255,255,0.15)' }}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${organizationMode === 'agency' ? 'bg-teal-500 text-white' : 'bg-white/15 text-white/70'}`}>
                      <i className="ri-building-4-line text-base"></i>
                    </div>
                    <div className="text-xs font-semibold text-white leading-tight">{t('hero_agency_organize')}</div>
                  </button>
                </div>
              </div>

              {/* Step 3 */}
              <div className="mb-4">
                <label className="block text-left text-xs font-semibold text-white/70 mb-2 uppercase tracking-wider">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-rose-500 text-white text-[10px] rounded-full mr-2">3</span>
                  {organizationMode === 'agency' ? t('hero_step3_agency') : t('hero_step3_institution')}
                  <span className="normal-case font-normal text-white/40 ml-1">{t('hero_step3_optional')}</span>
                </label>
                <div className="flex items-center px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)' }}>
                  <i className={`${organizationMode === 'agency' ? 'ri-building-4-line' : 'ri-hospital-line'} text-white/50 mr-3 flex-shrink-0 text-sm`}></i>
                  {organizationMode === 'agency' ? (
                    <select value={selectedAgency} onChange={(e) => setSelectedAgency(e.target.value)} className="flex-1 text-sm outline-none bg-transparent cursor-pointer text-white">
                      <option value="" className="text-gray-900 bg-white">{t('hero_all_agencies')}</option>
                      {loadingAgencies ? (
                        <option disabled className="text-gray-900 bg-white">{t('loading')}</option>
                      ) : agencies.map((agency) => (
                        <option key={agency.id} value={agency.id} className="text-gray-900 bg-white">
                          {agency.name}{agency.city ? ` (${agency.city})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select value={selectedHealthInstitution} onChange={(e) => setSelectedHealthInstitution(e.target.value)} className="flex-1 text-sm outline-none bg-transparent cursor-pointer text-white">
                      <option value="" className="text-gray-900 bg-white">{t('hero_all_institutions')}</option>
                      {loadingInstitutions ? (
                        <option disabled className="text-gray-900 bg-white">{t('loading')}</option>
                      ) : healthInstitutions.map((inst) => (
                        <option key={inst.id} value={inst.id} className="text-gray-900 bg-white">
                          {inst.name}{inst.city ? ` (${inst.city})` : ''}{inst.is_pro_member ? ' ⭐' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* City */}
              <div className="mb-5">
                <label className="block text-left text-xs font-semibold text-white/70 mb-2 uppercase tracking-wider">
                  {t('hero_city_label')} <span className="normal-case font-normal text-white/40 ml-1">{t('hero_step3_optional')}</span>
                </label>
                <div className="flex items-center px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)' }}>
                  <i className="ri-map-pin-line text-white/50 mr-3 flex-shrink-0 text-sm"></i>
                  <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} className="flex-1 text-sm outline-none bg-transparent cursor-pointer text-white">
                    <option value="" className="text-gray-900 bg-white">{t('hero_all_cities')}</option>
                    {cities.map((city) => (
                      <option key={city} value={city} className="text-gray-900 bg-white">{city}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={clearAll}
                  className="px-4 py-3.5 rounded-xl font-medium text-sm whitespace-nowrap cursor-pointer transition-all text-white/70 hover:text-white"
                  style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.20)' }}
                >
                  <i className="ri-refresh-line"></i>
                </button>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={!selectedTreatment}
                  className="flex-1 px-6 py-3.5 bg-gradient-to-r from-rose-500 to-rose-400 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-rose-500 transition-all text-sm whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  style={{ boxShadow: '0 4px 24px rgba(244,63,94,0.40)' }}
                >
                  <i className="ri-search-line mr-2"></i>
                  {organizationMode === 'agency' ? t('hero_search_agencies') : t('hero_search_institutions')}
                </button>
              </div>

              <p className="text-center text-xs text-white/35 mt-4">
                <i className="ri-shield-check-line mr-1 text-emerald-400/70"></i>
                {t('hero_trust_note')}
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
